import { signal, computed, effect } from "@preact/signals";
import { CalculatorState, SavedCalculation, InputMode } from "../logic/types";
import { calculateDeduction } from "../logic/calculator";

const STORAGE_KEY = "company-car-calc-settings";
const SAVES_STORAGE_KEY = "company-car-calc-saves";

export const defaultInputs = {
  carPrice: 500000,
  residualValuePercent: 40,
  marginalTaxPercent: 50,
  benefitValue: 5000,
  workDrivingKm: 0,
  inputMode: "FROM_PRICE" as InputMode,
  leaseCostExclVat: 4000,
};

const defaultSettings = {
  annualInterestRate: 5.0,
  monthlyExtraCosts: 1000,
  promotionDeduction: 1000,
  taxFreeMileageAllowance: 2.5,
  taxedMileageAllowance: 1.2,
  fuelCompensationRate: 1.2,
};

const savedSettings = localStorage.getItem(STORAGE_KEY);
const initialSettings = savedSettings
  ? { ...defaultSettings, ...JSON.parse(savedSettings) }
  : defaultSettings;

const savedCalculationsRaw = localStorage.getItem(SAVES_STORAGE_KEY);
const initialSavedCalculations: SavedCalculation[] = savedCalculationsRaw
  ? JSON.parse(savedCalculationsRaw).map((calc: any) => ({
      ...calc,
      inputs: { ...defaultInputs, ...calc.inputs },
    }))
  : [];

export const inputs = signal({ ...defaultInputs });

export const settings = signal(initialSettings);

export const savedCalculations = signal<SavedCalculation[]>(
  initialSavedCalculations,
);

// Persistence
effect(() => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings.value));
});

effect(() => {
  localStorage.setItem(
    SAVES_STORAGE_KEY,
    JSON.stringify(savedCalculations.value),
  );
});

export const calculationResult = computed(() => {
  const state: CalculatorState = {
    inputs: inputs.value,
    settings: settings.value,
  };
  return calculateDeduction(state);
});

export const updateInput = <K extends keyof typeof inputs.value>(
  key: K,
  value: (typeof inputs.value)[K],
) => {
  let validatedValue = value;
  if (key === "residualValuePercent") {
    validatedValue = Math.min(
      50,
      Math.max(30, validatedValue as number),
    ) as (typeof inputs.value)[K];
  } else if (key === "benefitValue") {
    validatedValue = Math.min(
      10000,
      Math.max(0, validatedValue as number),
    ) as (typeof inputs.value)[K];
  } else if (key === "carPrice") {
    validatedValue = Math.min(
      1000000,
      Math.max(300000, validatedValue as number),
    ) as (typeof inputs.value)[K];
  } else if (key === "marginalTaxPercent") {
    validatedValue = Math.min(
      55,
      Math.max(30, validatedValue as number),
    ) as (typeof inputs.value)[K];
  } else if (key === "workDrivingKm") {
    validatedValue = Math.min(
      2500,
      Math.max(0, validatedValue as number),
    ) as (typeof inputs.value)[K];
  } else if (key === "leaseCostExclVat") {
    validatedValue = Math.min(
      20000,
      Math.max(1000, validatedValue as number),
    ) as (typeof inputs.value)[K];
  }
  inputs.value = { ...inputs.value, [key]: validatedValue };
};

export const updateSetting = <K extends keyof typeof settings.value>(
  key: K,
  value: any,
) => {
  if (value === "") {
    settings.value = {
      ...settings.value,
      [key]: (defaultSettings as any)[key],
    };
    return;
  }
  let val = Number(value);
  if (isNaN(val)) val = (defaultSettings as any)[key];
  settings.value = { ...settings.value, [key]: val };
};

export const resetSettings = () => {
  localStorage.removeItem(STORAGE_KEY);
  settings.value = { ...defaultSettings };
};
